import React, { useState, useEffect } from 'react';
import { BookmarkPlus, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { serverUrl } from '@/authService';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useChecklist } from '@/contexts/ChecklistContext';

interface PublishButtonProps {
    queryHash: string;
}

const PublishButton: React.FC<PublishButtonProps> = ({ queryHash }) => {
    const [publishDialogOpen, setPublishDialogOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [category, setCategory] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [success, setSuccess] = useState<boolean>(false);
    const [publishedSlug, setPublishedSlug] = useState<string>('');
    const [categories, setCategories] = useState<string[]>([]);
    const { markStepComplete } = useChecklist();
    // Fetch categories when component mounts
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${serverUrl}/reviews/categories-list`);
                if (response.status === 200) {
                    setCategories(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch categories:', error);
            }
        };

        fetchCategories();
    }, []);

    const handlePublishClick = () => {
        setPublishDialogOpen(true);
        setSuccess(false);
    };

    const publishQuestion = async () => {
        if (!category.trim()) {
            toast.error('Category is required');
            return;
        }

        setIsLoading(true);
        try {
            markStepComplete('share_result');
            const response = await axios.post(
                `${serverUrl}/reviews/create-question`,
                {
                    query_hash: queryHash,
                    category: category.trim(),
                    description: description.trim() || undefined,
                    search_volume: 1000
                },
                { withCredentials: true }
            );

            if (response.status !== 200 && response.status !== 201) {
                throw new Error('Failed to publish question');
            }

            const data = response.data;
            setPublishedSlug(data.slug);
            setSuccess(true);
            toast.success('Question published successfully');
        } catch (error) {
            toast.error('Failed to publish question');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const viewPublishedQuestion = () => {
        window.open(`/questions/${publishedSlug}`, '_blank');
    };

    const resetForm = () => {
        setCategory('');
        setDescription('');
        setSuccess(false);
    };

    const handleDialogClose = (open: boolean) => {
        setPublishDialogOpen(open);
        if (!open) {
            resetForm();
        }
    };

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={handlePublishClick}
                className="border-brandMaroon text-brandMaroon hover:bg-lightMaroon font-medium rounded-md flex items-center gap-2 transition-colors"
            >
                <BookmarkPlus className="w-4 h-4" />
                Share Publicly
            </Button>

            <Dialog open={publishDialogOpen} onOpenChange={handleDialogClose}>
                <DialogContent className="sm:max-w-md font-manrope">
                    <DialogHeader>
                        <DialogTitle className="text-brandMaroon font-semibold text-xl">
                            {success ? 'Question Published' : 'Share Publicly'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="p-4">
                        {success ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-green-600">
                                    <Check className="w-5 h-5" />
                                    <p className="font-medium">Question published successfully!</p>
                                </div>
                                <Button
                                    variant="default"
                                    onClick={viewPublishedQuestion}
                                    className="w-full bg-brandMaroon hover:bg-[#6a001a]"
                                >
                                    View Published Question
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        resetForm();
                                        setPublishDialogOpen(false);
                                    }}
                                    className="w-full"
                                >
                                    Close
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-amber-800">
                                        This will publish your literature review as a public question that anyone can view.
                                        Your name will be shown as the author.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="category" className="text-sm font-medium">
                                            Category <span className="text-red-500">*</span>
                                        </Label>
                                        <Select value={category} onValueChange={setCategory}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue placeholder="Select a category" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {categories.map((cat) => (
                                                    <SelectItem key={cat} value={cat}>
                                                        {cat}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>



                                    <div className="space-y-2">
                                        <Label htmlFor="description" className="text-sm font-medium">
                                            Description (optional)
                                        </Label>
                                        <Textarea
                                            id="description"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Add a brief description for this question..."
                                            className="border-gray-300 focus:border-brandMaroon focus:ring-brandMaroon resize-none min-h-[100px]"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-2 pt-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => setPublishDialogOpen(false)}
                                        className="border-gray-300"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="default"
                                        onClick={publishQuestion}
                                        disabled={isLoading || !category.trim()}
                                        className="bg-brandMaroon hover:bg-[#6a001a]"
                                    >
                                        {isLoading ? (
                                            <div className="flex items-center gap-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                <span>Publishing...</span>
                                            </div>
                                        ) : (
                                            'Publish Question'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default PublishButton; 